<script setup lang="ts">
// A single-word name would trip vue/multi-word-component-names
defineOptions({ name: 'ToggleSwitch' });

defineProps<{
  /** Accessible name: the switch has no visible text of its own */
  label: string;
  disabled?: boolean;
}>();

const checked = defineModel<boolean>({ default: false });
</script>

<template>
  <button
    class="switch"
    :class="{ 'switch--checked': checked }"
    type="button"
    role="switch"
    :aria-checked="checked"
    :aria-label="label"
    :disabled="disabled"
    @click="checked = !checked"
  >
    <span class="switch__thumb"></span>
  </button>
</template>

<style lang="scss">
.switch {
  background-color: $color-neutral-medium;
  border: none;
  border-radius: $radius-pill;
  cursor: pointer;
  flex-shrink: 0;
  height: 28px;
  padding: 2px;
  position: relative;
  transition: background-color 0.2s;
  width: 48px;

  // Widens the tap target past the track's visual bounds without affecting the layout.
  &::after {
    content: '';
    inset: -8px;
    position: absolute;
  }

  &--checked {
    background-color: $color-primary;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &__thumb {
    background-color: $color-white;
    border-radius: $radius-pill;
    box-shadow: $shadow-soft;
    display: block;
    height: 24px;
    transition: translate 0.2s;
    width: 24px;
  }

  &--checked &__thumb {
    translate: 20px 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .switch,
  .switch__thumb {
    transition: none;
  }
}
</style>
